import { useEffect, useState } from "react";
import { Button, Label, Modal, TextInput } from "flowbite-react";

import { SubmitHandler, useForm, UseFormClearErrors } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import { boolean, z } from "zod";

import { useGetCurrencyRates } from '../../../hooks/Utilities/useGetCurrencyRates';
import { useUtils } from '../../../hooks/useUtils';
import ApiService from '../../../services/ApiServiceClass';

import CustomThemeModal from "../../CustomThemes/CustomThemeModal";
import ModalCloseButton from "./ModalCloseButton";
import ModalFormHeader from "./ModalFormHeader";
import ModalFormTextInput from "./ModalFormTextInput";
import ModalFormSupplierField from "./ModalFormSupplierField";
import ModalFormNumericInput from "./ModalFormNumericInput";
import ModalFormCurrencySelector from "./ModalFormCurrencySelector";
import ModalFormReadOnlyTextInput from "./ModalFormReadOnlyTextInput";
import ModalFormCheckbox from "./ModalFormCheckbox";
import ModalFormFileField from "./ModalFormFileField";
import { RequestItemType, initialRequestItem } from "./RequestItemType";
import { useLoader } from "../../../hooks/useLoader";
import { AxiosResponse } from "axios";

interface UpdateItemsFormModalProps {
    openModal: boolean,    
    setOpenModal: (value: boolean) => void,
    currentAction: string,
    selectedItemID: number,
    selectedItem: RequestItemType,
    requestID: number,    
    setRequestItems: React.Dispatch<React.SetStateAction<RequestItemType[]>>;
    fetchItems: () => Promise<AxiosResponse<any, any>>;
}

const MAX_FILE_SIZE = 3 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['application/pdf'];

const UpdateItemsFormModalSchema = z.object({
    itemName: z.string({
        required_error: "Must not be empty",
    }).min(1, { message: "Item name should not be empty." }),
    supplierPartNo: z.string(),
    url: z.string(),
    supplier: z.string({
        required_error: "Must not be empty",
    }).min(1, { message: "Supplier should not be empty." }),
    price: z.number(),
    convertedPrice: z.string(),    
    currency: z.string(),
    isGSTIncluded: z.boolean(),
    fileToUpload: z
        .any()
        .optional()
        .refine(file => file.length == 1 ? ACCEPTED_FILE_TYPES.includes(file?.[0]?.type) ? true : false : true, 'Invalid file. choose PDF.')
        .refine(file => file.length == 1 ? file[0]?.size <= MAX_FILE_SIZE ? true : false : true, 'Max file size allowed is 8MB.')
});

type UpdateItemsFormModalType = z.infer<typeof UpdateItemsFormModalSchema>;


const UpdateItemsFormModal = ({openModal, setOpenModal, currentAction, selectedItemID, selectedItem, requestID, fetchItems}: UpdateItemsFormModalProps) => {
    const { currencyRates, setCurrencyRates } = useGetCurrencyRates();
    const [currentDownloadFileID, setCurrentDownloadFileID] = useState(1);
    const [currentDownloadFileName, setCurrentDownloadFileName] = useState(""); 
    const [isWithQuote, setIsWithQuote ] = useState(false);
    const loader = useLoader();
    const { register, handleSubmit, watch, reset, getValues, setValue, setFocus, formState: { errors, isSubmitting } } = useForm<UpdateItemsFormModalType>({
        mode: "onChange",
        resolver: zodResolver(UpdateItemsFormModalSchema)
    });

    const helperUtils = useUtils(); 

    const onInvalid = (errors: any) => {
        console.log(errors);
    }

    const loadItems = async () => {
        try {
            loader?.setIsLoading(true);
            const itemsDO = await fetchItems();
            loader?.setIsLoading(false);
        } catch (error) {
            console.log(error);
            loader?.setIsLoading(false);
        }
    }

    const submitFormAdd = (form: FormData) => {        
        try{
            loader?.setIsLoading(true);
            ApiService.addItem(form)
            .then(() => {
                loader?.setIsLoading(false);
                loadItems();
                setOpenModal(false);
            })
            .catch((error) => {
                console.log(error);
            });
        } catch (error) {

        } finally {

        }
    }

    const submitFormEdit = (form: FormData) => {        
        try {
            loader?.setIsLoading(true);
            ApiService.updateItem(form)
                .then(() => {                        
                    loader?.setIsLoading(false);
                    loadItems();
                    setOpenModal(false);
                })
                .catch((error) => {
                    console.log(error);
                });
        } catch (error) {
            loader?.setIsLoading(false);
            console.error(error);
        } finally {
            loader?.setIsLoading(false);
        }
    }

    const onSubmitInner: SubmitHandler<UpdateItemsFormModalType> = (data) => {

        const formData = new FormData();

        formData.append('requestID', requestID.toString());
        formData.append('itemID', selectedItemID.toString());
        formData.append('itemName', data.itemName);
        formData.append('price', data.price.toString());
        formData.append('currency', data.currency);
        formData.append('convertedPrice', data.convertedPrice);
        formData.append('url', data.url);
        formData.append('supplier', data.supplier);
        formData.append('supplierPartNo', data.supplierPartNo);
        formData.append('isGSTIncluded', data.isGSTIncluded.toString());
        formData.append('fileToUpload', data.fileToUpload[0]);
        
        if (currentAction === "add") {
            submitFormAdd(formData);
        } else {
            formData.append('withQuote', isWithQuote.toString());
            submitFormEdit(formData);
        }
    }

    const fileToUpload = watch("fileToUpload");
    const currency = watch("currency");
    const price = watch("price");
    const isGSTIncluded = watch("isGSTIncluded");

    const computeTotalInNZD = () => {
        let gstValue = 1.15;
        let convertedAmount = 0;
        let conversionFactor = 0;

        if (isGSTIncluded) {
            gstValue = 1.15;
        } else {
            gstValue = 1;
        }

        if (currencyRates.length > 0) {
            const baseCurrencyObj = currencyRates.find((item: any) => item.currency == "NZD");
            const currencyToConvertObj = currencyRates.find((item: any) => item.currency == currency);

            const baseCurrencyRate = baseCurrencyObj?.rate ? baseCurrencyObj.rate : 1;
            const currencyToConvertRate = currencyToConvertObj?.rate ? currencyToConvertObj.rate : 1;

            conversionFactor = (1 / currencyToConvertRate) * baseCurrencyRate;            
        } else {
            conversionFactor = 1;
        }

        convertedAmount = (price * conversionFactor) / gstValue;

        setValue("convertedPrice", convertedAmount.toFixed(2).toString())
    }

    useEffect(() => {
        if (currency) {
            if (currency === "NZD") {
                setValue("isGSTIncluded", true);
            } else {
                setValue("isGSTIncluded", false);
            }
        }
    }, [currency]);

    useEffect(() => {
        if (price) {
            computeTotalInNZD();
        }
    }, [price, isGSTIncluded]);

    useEffect(() =>{
        if (openModal) {
            reset();
            if (currentAction === 'add') {
                if (currency === "NZD") {
                    setValue("isGSTIncluded", true);
                } else {
                    setValue("isGSTIncluded", false);
                }                
            }

            if (currentAction === 'edit') {                
                setValue("isGSTIncluded", !!selectedItem.is_gst_included);
                setValue("currency", selectedItem.currency);
                setValue("itemName", selectedItem.name);
                setValue("supplier", selectedItem.supplier);
                setValue("url", selectedItem.url);
                setValue("supplierPartNo", selectedItem.supplierPartNo);
                setValue("price", parseFloat(selectedItem.price.toFixed(2)));
                setValue("convertedPrice", selectedItem.price_nzd.toFixed(2).toString());
                if (selectedItem.with_quote == 1) {
                    setIsWithQuote(true);
                    setCurrentDownloadFileID(selectedItem.quote.id);
                    setCurrentDownloadFileName(selectedItem.quote.filename);
                }
            }
        } 
    }, [openModal]);
    return (
            <Modal 
                show={openModal} 
                onClose={() => setOpenModal(false)} 
                size={'lg'} 
                theme={CustomThemeModal}
            >
                <Modal.Body>

                    <ModalCloseButton setOpenModal={setOpenModal} />

                    <form onSubmit={(e) => {
                            e.stopPropagation();
                            return handleSubmit(onSubmitInner, onInvalid)(e);
                        }}
                    >
                        <div className="space-y-6">

                            <ModalFormHeader currentAction={currentAction} />

                            <ModalFormTextInput 
                                        register={register("itemName")}
                                        fieldName={"itemName"}
                                        label={"Item Name"}
                                        errors={errors}
                                        placeholder={"Enter Name of Item"}
                            />

                            {/* SUPPLIER BLOCK */}
                            <div className="grid grid-cols-3 gap-4">
                                <ModalFormSupplierField
                                    register={register("supplier")}
                                    fieldName={"supplier"}
                                    label={"Supplier"}
                                    errors={errors}
                                    placeholder={"Enter or Lookup Supplier"}
                                    setValue={setValue}
                                    setFocus={setFocus}
                                />
                                <ModalFormTextInput 
                                        register={register("supplierPartNo")}
                                        fieldName={"supplierPartNo"}
                                        label={"PartNo/SKU"}
                                        errors={errors}
                                        placeholder={"Enter PartNo/SKU"}
                                />
                            </div>

                            {/* PRICE BLOCK */}
                            <div className="grid grid-cols-2 gap-4">
                                <ModalFormNumericInput 
                                    register={register("price", { valueAsNumber: true })}
                                    fieldName={"price"}
                                    label={"Price"}
                                    errors={errors}
                                    placeholder={"Enter Price"}
                                />

                                <ModalFormCurrencySelector
                                    register={register("currency")}
                                    fieldName={"currency"}
                                    label={"Currency"}
                                    errors={errors}
                                />
                            </div>

                            <ModalFormCheckbox 
                                register={register("isGSTIncluded")}
                                fieldName={"isGSTIncluded"}
                                label={"GST Included"}                                
                            />

                            <ModalFormReadOnlyTextInput
                                register={register("convertedPrice")}
                                fieldName={"convertedPrice"}
                                label={"Converted Price (NZ$) ex. GST**”**"}
                                placeholder={"Converted Price"}
                            />

                            <ModalFormTextInput 
                                register={register("url")}
                                fieldName={"url"}
                                label={"URL Quote of Item (Optional):"}
                                errors={errors}
                                placeholder={"URL Quote of Item"}
                            />

                            <ModalFormFileField 
                                register={register("fileToUpload")} 
                                currentAction={currentAction} 
                                fileToUpload={fileToUpload}
                                isWithQuote={isWithQuote}
                                currentDownloadFileID={currentDownloadFileID}
                                currentDownloadFileName={currentDownloadFileName}
                            />

                            <div className="w-full">
                                <Button type="submit">
                                    {currentAction == "add" && "Add Item"}
                                    {currentAction == "edit" && "Update Item"}
                                </Button>
                            </div>
                        </div>
                    </form>                    
                </Modal.Body>
            </Modal>
    )
}

export default UpdateItemsFormModal;